import { Title } from '@angular/platform-browser';
import {
  Component,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { AuthenticationService } from '@app/auth';
import { environment } from '@env/environment';
import { slide } from '@app/animations';
import { ProfileService } from '../profile/profile.service';
import { I18nService } from '@app/i18n/i18n.service';

import { Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { LanguageService } from '@core/services/language.service';
import { ProfileDetails } from '@app/profile/profile.types';
import { ProfileDetails as ProfileDetailsResponse } from '@app/profile/profile.service';
import { CoreService } from '@app/@core/services/core.service';
import { CredentialsService } from '@core/services/credentials.service';
import { NumberTranslatePipe } from '@app/@shared/pipes/number-translate/number-translate.pipe';
import { AnalyticsService } from '@app/ahoy-analytics/analytics.service';

interface MenuOption {
  label: string;
  icon: string;
  iconSelected: string;
  link: string;
  badge?: string;
}

@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  animations: [slide],
  providers: [
    NumberTranslatePipe,
    {
      provide: LOCALE_ID,
      deps: [I18nService],
      useFactory: (i18nService: I18nService) => {
        return i18nService.language;
      },
    },
  ],
  standalone: false,
})
export class ShellComponent implements OnInit {
  version: string | null = environment.version;
  year = new Date().getFullYear();
  selectedLanguage: string;
  poweredByUrl: string;
  profileDetails: ProfileDetails = {
    imgUrl: '',
    name: '',
    mobile: '',
    availableAppCredit: {
      amount: 0,
      currency: '',
    },
  };
  date = new Date();
  timezone = Intl.DateTimeFormat(undefined, { timeZoneName: 'long' })
    .formatToParts(this.date)
    .find((part) => part.type == 'timeZoneName').value;

  sectionMenuOptions: MenuOption[] = [
    {
      label: 'Dashboard',
      icon: '/assets/side-menu/dashboard-icon.svg',
      iconSelected: '/assets/side-menu/selected-dashboard-icon.svg',
      link: '/dashboard',
    },
    {
      label: 'Bookings',
      icon: '/assets/side-menu/booking-icon.svg',
      iconSelected: '/assets/side-menu/selected-booking-icon.svg',
      link: '/bookings',
    },
  ];
  otherMenuOptions: MenuOption[] = [
    {
      label: 'Help Center',
      icon: '/assets/side-menu/info.svg',
      iconSelected: '/assets/side-menu/info.svg',
      link: '/help-center',
    },
  ];
  _isMobile!: boolean;
  dir!: string;
  enableChatbot = environment.enableChatbot;
  constructor(
    private router: Router,
    private titleService: Title,
    private authenticationService: AuthenticationService,
    private credentialsService: CredentialsService,
    private coreService: CoreService,
    private breakpointObserver: BreakpointObserver,
    private profileService: ProfileService,
    private i18nService: I18nService,
    private languageService: LanguageService,
    @Inject(DOCUMENT) private document: Document,
    private analyticsService: AnalyticsService,
  ) {
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this._isMobile = result.matches;
      });
  }

  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.profileService
      .getProfileDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: ProfileDetailsResponse) => {
        this.profileDetails.imgUrl = res.imageUrl;
        this.profileDetails.name = res.firstName + ' ' + res.lastName;
        this.profileDetails.email = res.email;
        this.profileDetails.mobile = res.phone;
        this.profileDetails.availableAppCredit.amount =
          res.availableAppCredit.amount;
        this.profileDetails.availableAppCredit.currency =
          res.availableAppCredit.currency;
        this.analyticsService.setUserId(String(res.id));
      });

    this.profileService.profileImageUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((imgUrl) => {
        this.profileDetails.imgUrl = imgUrl ?? '';
      });
    this.dir = localStorage.getItem('html-dir');

    this.coreService
      .getPoweredByImage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((url: string) => {
        this.poweredByUrl = url;
      });
  }

  changeLanguage(event: { value: string }) {
    this.selectedLanguage = event.value;
    this.coreService.showConfirmationDialog(
      'Change Language',
      'To change the language, the page will need to reload, and all unsaved changes will be lost.',
      '600px',
      '/assets/language-change.svg',
      'Change Language',
      this.switchLanguage.bind(this),
    );
  }

  switchLanguage() {
    this.i18nService.language = this.selectedLanguage;
    this.languageService.language = this.selectedLanguage;
    if (this.selectedLanguage !== this.document.dir) {
      this.changeHtmlDirection(this.selectedLanguage);
    }
    location.reload();
  }

  get currentLanguage(): string {
    return this.i18nService.language;
  }

  changeHtmlDirection(lang: string) {
    let htmlTag = this.document.getElementsByTagName(
      'html',
    )[0] as HTMLHtmlElement;
    this.dir = lang === 'ar' ? 'rtl' : 'ltr';
    htmlTag.dir = lang === 'ar' ? 'rtl' : 'ltr';
    localStorage.setItem('html-dir', this.dir);
  }

  selectedItem(event: MenuOption) {
    this.router.navigateByUrl(event.link);
  }

  logout() {
    this.authenticationService
      .logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.router.navigate(['/login'], { replaceUrl: true }));
  }

  get username(): string | null {
    const credentials = this.credentialsService.credentials;
    return credentials ? credentials.username : null;
  }

  get isMobile(): boolean {
    return this._isMobile;
  }

  get title(): string {
    return this.titleService.getTitle();
  }

  profile() {
    this.router.navigateByUrl('/profile');
  }
}
